// src/lib/pdfDocuments.ts
import { jsPDF } from 'jspdf';
import { getVoucherEffectiveStatus, getVoucherQrUrl, getVoucherUrl, type ShopVoucher } from './vouchers';
import { getOrderStatusLabel, getVoucherStatusLabel, getLanguageCode } from './transactionI18n';

type PdfLanguage = 'fr' | 'en' | string | undefined;

type PdfOrder = Record<string, any>;

const asText = (value: unknown, fallback = '—') => {
  const text = String(value ?? '').trim();
  return text || fallback;
};

const money = (value?: number | string | null, language?: PdfLanguage, options?: { cents?: boolean }) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return getLanguageCode(language) === 'en' ? 'To confirm' : 'À confirmer';
  const amount = options?.cents ? n / 100 : n > 1000 ? n / 100 : n;
  try {
    return new Intl.NumberFormat(getLanguageCode(language) === 'en' ? 'en-CA' : 'fr-CA', {
      style: 'currency',
      currency: 'CAD',
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} $`;
  }
};

const dateText = (value?: string | null, language?: PdfLanguage, withTime = false) => {
  if (!value) return getLanguageCode(language) === 'en' ? 'Unknown date' : 'Date inconnue';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return getLanguageCode(language) === 'en' ? 'Unknown date' : 'Date inconnue';
  return withTime
    ? date.toLocaleString(getLanguageCode(language) === 'en' ? 'en-CA' : 'fr-CA', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : date.toLocaleDateString(getLanguageCode(language) === 'en' ? 'en-CA' : 'fr-CA', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
};

const shortId = (value?: string | null) => {
  const raw = String(value || '').trim();
  return raw ? raw.slice(0, 10).toUpperCase() : 'FORFEO';
};

const safeFilePart = (value: unknown) =>
  String(value || 'forfeo')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80) || 'forfeo';

const getOrderItems = (order: PdfOrder) => {
  const candidates = [order?.items, order?.shop_order_items, order?.order_items, order?.line_items, order?.products];
  const list = candidates.find(Array.isArray);
  return Array.isArray(list) ? list : [];
};

const getOrderVouchers = (order: PdfOrder) => {
  const candidates = [order?.vouchers, order?.shop_order_vouchers, order?.order_vouchers];
  const list = candidates.find(Array.isArray);
  return Array.isArray(list) ? list : [];
};

const getItemTitle = (item: any) =>
  asText(item?.title || item?.product_title || item?.name || item?.description || item?.metadata?.title, 'Forfeo');

const getItemQuantity = (item: any) => Number(item?.quantity || item?.q || 1) || 1;

const getItemAmountCents = (item: any) => {
  const candidates = [item?.total_cents, item?.amount_cents, item?.unit_amount_cents, item?.price_cents, item?.total, item?.price];
  const found = candidates.find((value) => Number.isFinite(Number(value)));
  return Number(found || 0);
};

const getOrderTotal = (order: PdfOrder) =>
  Number(order?.total_cents || order?.amount_total_cents || order?.total_amount_cents || order?.total || order?.amount_total || 0);

const getOrderSubtotal = (order: PdfOrder) => Number(order?.subtotal_cents || order?.subtotal || order?.amount_subtotal || 0);
const getOrderTps = (order: PdfOrder) => Number(order?.tps_cents || order?.tax_tps_cents || order?.tps || 0);
const getOrderTvq = (order: PdfOrder) => Number(order?.tvq_cents || order?.tax_tvq_cents || order?.tvq || 0);

const drawHeader = (doc: jsPDF, title: string, subtitle: string) => {
  doc.setFillColor(6, 17, 32);
  doc.roundedRect(14, 12, 182, 34, 5, 5, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(17);
  doc.text(title, 24, 28);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(subtitle, 24, 37);
  doc.setTextColor(15, 23, 42);
};

const drawLabelValue = (doc: jsPDF, label: string, value: string, x: number, y: number, width = 80) => {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(100, 116, 139);
  doc.text(label.toUpperCase(), x, y);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(15, 23, 42);
  const lines = doc.splitTextToSize(value || '—', width);
  doc.text(lines.slice(0, 2), x, y + 6);
};

const fetchImageAsDataUrl = async (url: string) => {
  const res = await fetch(url, { mode: 'cors' });
  if (!res.ok) throw new Error('image_unavailable');
  const blob = await res.blob();
  return await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result || ''));
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

const drawFooter = (doc: jsPDF, language?: PdfLanguage) => {
  const isEn = getLanguageCode(language) === 'en';
  doc.setDrawColor(226, 232, 240);
  doc.line(14, 278, 196, 278);
  doc.setTextColor(100, 116, 139);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text(isEn ? 'Forfeo Explore · Secure document generated from your Forfeo account.' : 'Forfeo Explore · Document sécurisé généré depuis votre compte Forfeo.', 14, 285);
  doc.text('forfeoexplore.com', 160, 285);
};

export const downloadVoucherPdf = async (voucher: ShopVoucher | any, language?: PdfLanguage) => {
  const isEn = getLanguageCode(language) === 'en';
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
  const status = getVoucherEffectiveStatus(voucher);
  const token = voucher?.secure_token || voucher?.token || '';
  const voucherCode = asText(voucher?.voucher_code || voucher?.code, 'FE-FORFEO');
  const title = asText(voucher?.title || voucher?.item?.title || voucher?.metadata?.title, isEn ? 'Forfeo experience' : 'Expérience Forfeo');
  const publicUrl = token
    ? `${typeof window !== 'undefined' ? window.location.origin : 'https://forfeoexplore.com'}${getVoucherUrl(token)}`
    : 'https://forfeoexplore.com/mes-achats';

  drawHeader(
    doc,
    isEn ? 'Official Forfeo voucher' : 'Voucher Forfeo officiel',
    isEn ? 'Present this document to the partner when redeeming.' : 'Présentez ce document au partenaire au moment de l’utilisation.'
  );

  doc.setFillColor(status === 'active' ? 236 : 241, status === 'active' ? 253 : 245, status === 'active' ? 245 : 249);
  doc.roundedRect(14, 55, 182, 150, 7, 7, 'F');
  doc.setDrawColor(226, 232, 240);
  doc.roundedRect(14, 55, 182, 150, 7, 7, 'S');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(15, 23, 42);
  doc.text(doc.splitTextToSize(title, 110), 24, 75);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(26);
  doc.setTextColor(6, 17, 32);
  doc.text(voucherCode, 24, 106);

  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  doc.text(isEn ? 'Voucher code' : 'Code voucher', 24, 114);

  drawLabelValue(doc, isEn ? 'Status' : 'Statut', getVoucherStatusLabel(status, language), 24, 132, 55);
  drawLabelValue(doc, isEn ? 'Value' : 'Valeur', money(voucher?.amount_cents, language, { cents: true }), 90, 132, 50);
  drawLabelValue(doc, isEn ? 'Expires on' : 'Expiration', voucher?.expires_at ? dateText(voucher.expires_at, language) : isEn ? 'No date defined' : 'Aucune date définie', 145, 132, 42);

  drawLabelValue(doc, isEn ? 'Created on' : 'Créé le', dateText(voucher?.created_at, language), 24, 158, 55);
  drawLabelValue(doc, isEn ? 'Order' : 'Commande', `#${shortId(voucher?.order_id || voucher?.order?.id)}`, 90, 158, 50);
  drawLabelValue(doc, isEn ? 'Customer' : 'Client', asText(voucher?.customer_email || voucher?.order?.customer_email || voucher?.order?.email), 145, 158, 42);

  const qrUrl = token ? getVoucherQrUrl(token) : '';
  if (qrUrl) {
    try {
      const dataUrl = await fetchImageAsDataUrl(qrUrl);
      doc.addImage(dataUrl, 'PNG', 142, 70, 42, 42);
    } catch {
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(142, 70, 42, 42, 4, 4, 'F');
      doc.setFontSize(8);
      doc.setTextColor(100, 116, 139);
      doc.text(doc.splitTextToSize(isEn ? 'QR unavailable. Use the voucher code.' : 'QR indisponible. Utilisez le code voucher.', 34), 146, 88);
    }
  }

  doc.setFillColor(255, 255, 255);
  doc.roundedRect(24, 178, 162, 18, 4, 4, 'F');
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(51, 65, 85);
  doc.text(doc.splitTextToSize(publicUrl, 150), 30, 188);

  doc.setFillColor(6, 17, 32);
  doc.roundedRect(14, 216, 182, 34, 6, 6, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text(isEn ? 'Important' : 'Important', 24, 230);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(
    doc.splitTextToSize(
      isEn
        ? 'This voucher is protected by a unique secure token. Do not share this document except with the partner when redeeming your purchase.'
        : 'Ce voucher est protégé par un token sécurisé unique. Ne partagez pas ce document sauf avec le partenaire au moment d’utiliser votre achat.',
      160
    ),
    24,
    240
  );

  drawFooter(doc, language);
  doc.save(`forfeo-voucher-${safeFilePart(voucherCode)}.pdf`);
};

export const downloadOrderReceiptPdf = async (order: PdfOrder, language?: PdfLanguage) => {
  const isEn = getLanguageCode(language) === 'en';
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
  const items = getOrderItems(order);
  const vouchers = getOrderVouchers(order);
  const orderNumber = shortId(order?.id || order?.stripe_session_id);
  const total = getOrderTotal(order);
  const subtotal = getOrderSubtotal(order);
  const tps = getOrderTps(order);
  const tvq = getOrderTvq(order);

  drawHeader(
    doc,
    isEn ? 'Forfeo receipt' : 'Reçu Forfeo',
    isEn ? 'Official purchase summary generated from your Forfeo account.' : 'Résumé officiel de votre achat généré depuis votre compte Forfeo.'
  );

  doc.setFillColor(248, 250, 252);
  doc.roundedRect(14, 55, 182, 38, 6, 6, 'F');
  doc.setDrawColor(226, 232, 240);
  doc.roundedRect(14, 55, 182, 38, 6, 6, 'S');

  drawLabelValue(doc, isEn ? 'Order' : 'Commande', `#${orderNumber}`, 24, 70, 45);
  drawLabelValue(doc, isEn ? 'Status' : 'Statut', getOrderStatusLabel(order?.payment_status || order?.status, language), 76, 70, 45);
  drawLabelValue(doc, isEn ? 'Customer' : 'Client', asText(order?.customer_email || order?.email || order?.billing_email), 128, 70, 55);

  drawLabelValue(doc, isEn ? 'Created on' : 'Créée le', dateText(order?.created_at || order?.paid_at, language, true), 24, 106, 70);
  drawLabelValue(doc, isEn ? 'Stripe session' : 'Session Stripe', asText(order?.stripe_session_id, isEn ? 'Pending' : 'En attente'), 105, 106, 78);

  let y = 136;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(15, 23, 42);
  doc.text(isEn ? 'Items' : 'Articles', 14, y);
  y += 7;

  doc.setDrawColor(226, 232, 240);
  doc.line(14, y, 196, y);
  y += 8;

  if (items.length > 0) {
    items.slice(0, 10).forEach((item: any) => {
      const title = getItemTitle(item);
      const qty = getItemQuantity(item);
      const amount = money(getItemAmountCents(item), language, { cents: true });
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(15, 23, 42);
      doc.text(doc.splitTextToSize(title, 110).slice(0, 2), 16, y);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 116, 139);
      doc.text(`${isEn ? 'Qty' : 'Qté'}: ${qty}`, 16, y + 8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 23, 42);
      doc.text(amount, 170, y, { align: 'right' });
      y += 18;
      if (y > 235) {
        doc.addPage();
        y = 24;
      }
    });
  } else {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    doc.text(isEn ? 'Item details are not available for this order.' : 'Les détails des articles ne sont pas disponibles pour cette commande.', 16, y);
    y += 14;
  }

  y += 4;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(15, 23, 42);
  doc.text(isEn ? 'Vouchers' : 'Vouchers', 14, y);
  y += 7;
  doc.line(14, y, 196, y);
  y += 8;

  if (vouchers.length > 0) {
    vouchers.slice(0, 12).forEach((voucher: any) => {
      const status = getVoucherEffectiveStatus(voucher);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(15, 23, 42);
      doc.text(asText(voucher?.voucher_code, 'FE-FORFEO'), 16, y);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 116, 139);
      doc.text(getVoucherStatusLabel(status, language), 68, y);
      doc.text(asText(voucher?.title || voucher?.item?.title, isEn ? 'Forfeo voucher' : 'Voucher Forfeo'), 110, y, { maxWidth: 70 });
      y += 8;
      if (y > 235) {
        doc.addPage();
        y = 24;
      }
    });
  } else {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    doc.text(isEn ? 'No voucher is currently attached to this order.' : 'Aucun voucher n’est actuellement attaché à cette commande.', 16, y);
    y += 14;
  }

  const boxY = Math.min(y + 10, 226);
  doc.setFillColor(248, 250, 252);
  doc.roundedRect(112, boxY, 84, 38, 5, 5, 'F');
  doc.setDrawColor(226, 232, 240);
  doc.roundedRect(112, boxY, 84, 38, 5, 5, 'S');
  doc.setFontSize(9);
  doc.setTextColor(71, 85, 105);
  doc.text(isEn ? 'Subtotal' : 'Sous-total', 120, boxY + 10);
  doc.text(money(subtotal, language, { cents: true }), 188, boxY + 10, { align: 'right' });
  doc.text('TPS (5%)', 120, boxY + 18);
  doc.text(money(tps, language, { cents: true }), 188, boxY + 18, { align: 'right' });
  doc.text('TVQ (9,975%)', 120, boxY + 26);
  doc.text(money(tvq, language, { cents: true }), 188, boxY + 26, { align: 'right' });
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(15, 23, 42);
  doc.text(isEn ? 'Total' : 'Total', 120, boxY + 34);
  doc.text(money(total, language, { cents: true }), 188, boxY + 34, { align: 'right' });

  drawFooter(doc, language);
  doc.save(`forfeo-recu-${safeFilePart(orderNumber)}.pdf`);
};
